import { useEffect, useRef, useState } from "react";

import { Modal } from "ui/components/Modal";
import ModalHeader from "ui/components/Modal/ModalHeader";
import ModalBody from "ui/components/Modal/ModalBody";

import { Wizard } from "ui/components/Wizard";
import WizardStep from "ui/components/Wizard/WizardStep";
import WizardFooter from "ui/components/Wizard/WizardFooter";

import { validateYup } from "services/forms/validation/validation";
import { Errors } from "services/forms/validation/types";

import { ResidualValueFormValues, AdditionalEquipment, ResidualValueResponse } from "services/api/catalog/cvh/types";
import { initialResidualValueFormValues } from "services/api/catalog/cvh/consts";
import { RESIDUAL_VALUE_STEP1_SCHEMA } from "services/api/catalog/cvh/validation";
import { getAdditionalEquipmentByJomAPI, calculateResidualValueAPI } from "services/api/catalog/cvh/api";
import { FleetVehicleEditFormValues } from "services/api/fleet/vehicles/types";
import { initialFleetVehicleEditForm } from "services/api/fleet/vehicles/consts";
import { VehicleInfo } from "ui/modules/fleet/vehicles/pages/VehicleDetailPage/components/VehicleDetailCard/components/VehicleEditCard/components/VehicleSummaryCard/types";
import { initialVehicleInfo } from "ui/modules/fleet/vehicles/pages/VehicleDetailPage/components/VehicleDetailCard/components/VehicleEditCard/consts";

import { VehicleResidualValueModalProps } from "./types";
import ResidualValueBasicDataForm from "./components/ResidualValueBasicDataForm";
import ResidualValueVehicleDetailsForm from "./components/ResidualValueVehicleDetailsForm";
import ResidualValueEquipmentForm from "./components/ResidualValueEquipmentForm";
import ResidualValueResultModal from "./components/ResidualValueResult";
import ResidualValueIntro from "./components/ResidualValueIntro/ResidualValueIntro";


const VehicleResidualValueModal: React.FC<VehicleResidualValueModalProps> = ({
  show,
  onClose,
}) => {
  const [formValues, setFormValues] = useState<ResidualValueFormValues>(initialResidualValueFormValues);
  const [errors, setErrors] = useState<Errors>({});
  const [stepCurrent, setStepCurrent] = useState(0);
  const [isLoading, setIsLoading] = useState(false);

  const [equipment, setEquipment] = useState<AdditionalEquipment[]>([]);
  const [equipmentLoading, setEquipmentLoading] = useState(false);
  const [selectedEquipment, setSelectedEquipment] = useState<number[]>([]);

  const [result, setResult] = useState<ResidualValueResponse | null>(null);

  // Lifted picker state so it survives step navigation
  const [pickerFormValues, setPickerFormValues] = useState<FleetVehicleEditFormValues>(initialFleetVehicleEditForm);
  const [pickerErrors, setPickerErrors] = useState<Errors>({});
  const [vehicleInfo, setVehicleInfo] = useState<VehicleInfo>(initialVehicleInfo);

  const lastFetchedJom = useRef<string | null>(null);

  useEffect(() => {
    if (!show) {
      setFormValues(initialResidualValueFormValues);
      setErrors({});
      setStepCurrent(0);
      setEquipment([]);
      setSelectedEquipment([]);
      setResult(null);
      setPickerFormValues(initialFleetVehicleEditForm);
      setPickerErrors({});
      setVehicleInfo(initialVehicleInfo);
      lastFetchedJom.current = null;
    }
  }, [show]);

  /*
    Greška o JOM-u postavlja se na "Nastavi", a dosad se brisala tek sljedećim klikom. Picker pri
    odabiru briše samo svoje greške, pa je poruka ostajala i kad je vozilo već odabrano. Čim JOM
    stigne, odabirom ili pretragom po JOM-u, stara poruka nestaje sama.
  */
  useEffect(() => {
    if (!formValues.jom) return;

    setErrors((prev) => {
      if (!prev.jom) return prev;

      const { jom, ...rest } = prev;
      return rest;
    });
  }, [formValues.jom]);

  const fetchEquipment = async (jom: string) => {
    setEquipmentLoading(true);
    try {
      const data = await getAdditionalEquipmentByJomAPI(jom);
      setEquipment(data);
      setSelectedEquipment(data.filter((e: AdditionalEquipment) => e.included).map((e: AdditionalEquipment) => e.id));
    } catch {
      setEquipment([]);
    }
    setEquipmentLoading(false);
  };

  const handleNextStep = async () => {
    if (stepCurrent === 0) {
      const isValid = validateYup(formValues, RESIDUAL_VALUE_STEP1_SCHEMA, setErrors);
      if (!isValid) return;

      setStepCurrent(1);
      return;
    }

    if (stepCurrent === 1) {
      setStepCurrent(2);

      // Oprema ovisi o odabranom vozilu, pa se dohvaća tek kad se JOM promijeni.
      if (formValues.jom && formValues.jom !== lastFetchedJom.current) {
        lastFetchedJom.current = formValues.jom;
        setSelectedEquipment([]);
        fetchEquipment(formValues.jom);
      }
    }
  };

  const handleBackStep = () => {
    setStepCurrent((prev) => prev - 1);
  };

  const handleFinish = async () => {
    const requestValues: ResidualValueFormValues = {
      ...formValues,
      additionalEquipment: selectedEquipment,
    };

    setIsLoading(true);
    try {
      const response = await calculateResidualValueAPI(requestValues);
      setResult(response);
    } catch {
      // error handled by snackbar in createApiCall
    }
    setIsLoading(false);
  };

  const handleClose = () => {
    onClose();
  };

  /*
    Zatvaranje rezultata gasi i sam kalkulator. Iza rezultata nema koraka za dovršiti, pa bi
    povratak na čarobnjak s popunjenim poljima izgledao kao da izračun nije prošao.
  */
  const handleResultClose = () => {
    setResult(null);
    onClose();
  };

  const steps = [
    <WizardStep
      key={0}
      stepNumber={0}
      stepCurrent={stepCurrent}
      stepTitle="Osnovni podaci"
    >
      <ResidualValueBasicDataForm
        formValues={formValues}
        setFormValues={setFormValues}
        errors={errors}
        pickerFormValues={pickerFormValues}
        setPickerFormValues={setPickerFormValues}
        pickerErrors={pickerErrors}
        setPickerErrors={setPickerErrors}
        vehicleInfo={vehicleInfo}
        setVehicleInfo={setVehicleInfo}
      />
    </WizardStep>,

    <WizardStep
      key={1}
      stepNumber={1}
      stepCurrent={stepCurrent}
      stepTitle="Detalji vozila"
    >
      <ResidualValueVehicleDetailsForm
        show={show}
        formValues={formValues}
        setFormValues={setFormValues}
        errors={errors}
      />
    </WizardStep>,

    <WizardStep
      key={2}
      stepNumber={2}
      stepCurrent={stepCurrent}
      stepTitle="Dodatna oprema"
    >
      <ResidualValueEquipmentForm
        equipment={equipment}
        equipmentLoading={equipmentLoading}
        selectedEquipment={selectedEquipment}
        setSelectedEquipment={setSelectedEquipment}
        formValues={formValues}
        setFormValues={setFormValues}
      />
    </WizardStep>,
  ];

  return (
    <>
      <Modal
        show={show}
        position="side"
        size="medium"
        onHide={handleClose}
        intro={{
          content: (
            <ResidualValueIntro
              formValues={formValues}
              vehicleInfo={vehicleInfo}
              equipment={equipment}
              selectedEquipment={selectedEquipment}
              stepCurrent={stepCurrent}
              stepCount={steps.length}
            />
          ),
        }}
      >
        <ModalHeader
          icon="/media/images/add-icon.svg"
          title="Ostatak vrijednost"
          onHide={handleClose}
        />
        <ModalBody>
          <Wizard steps={steps} />
        </ModalBody>
        <WizardFooter
          stepCurrent={stepCurrent}
          steps={steps}
          onNextStep={handleNextStep}
          onBackStep={handleBackStep}
          onFinish={handleFinish}
          isLoading={isLoading}
          isReadOnly={false}
        />
      </Modal>
      <ResidualValueResultModal result={result} onClose={handleResultClose} />
    </>
  );
};

export default VehicleResidualValueModal;
